import FilterSegmentField from '@/components/clients/filter-segment';
import PhoneCountryExclusionField from '@/components/clients/phone-country-exclusion-field';
import { Button } from '@/components/ui/button';
import { Dialog, DialogClose, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import type { FilterSchema } from '@/entities/clients-categories';
import type { IClientsFilter, TriFilter } from '@/entities/clients-filter.entity';

const TAKE_STATUS_OPTIONS = [
	{ value: 'all' as const, label: 'Все' },
	{ value: 0 as const, label: 'Не вбит' },
	{ value: 1 as const, label: 'На вбиве' },
	{ value: 2 as const, label: 'Успешно' },
	{ value: 3 as const, label: 'Говно' },
];

const TG_STATUS_OPTIONS = [
	{ value: 'all' as const, label: 'Все' },
	{ value: 'notset' as const, label: 'Не исп.' },
	{ value: 'used' as const, label: 'Used' },
	{ value: 'woutcode' as const, label: 'Нет кода' },
	{ value: '2fa' as const, label: '2FA' },
];

const TRI_OPTIONS: { value: TriFilter; label: string }[] = [
	{ value: 'all', label: 'Все' },
	{ value: 'yes', label: 'Есть' },
	{ value: 'no', label: 'Нет' },
];

type TakeStatusValue = (typeof TAKE_STATUS_OPTIONS)[number]['value'];
type TgStatusValue = (typeof TG_STATUS_OPTIONS)[number]['value'];

interface ClientsFiltersSheetProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	filter: IClientsFilter;
	filterSchema: FilterSchema;
	activeFilters: number;
	onUpdate: (patch: Partial<IClientsFilter>) => void;
	onReset: () => void;
}

export default function ClientsFiltersSheet({
	open,
	onOpenChange,
	filter,
	filterSchema,
	activeFilters,
	onUpdate,
	onReset,
}: ClientsFiltersSheetProps) {
	const phoneCountryMode = filterSchema === 'phone' ? 'simRow' : 'client';

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent
				showCloseButton
				className='top-auto bottom-0 left-1/2 translate-x-[-50%] translate-y-0 w-full max-w-[460px] rounded-b-none rounded-t-xl p-0 gap-0 max-h-[85vh] flex flex-col overflow-hidden pb-[env(safe-area-inset-bottom)] data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom data-[state=closed]:zoom-out-100 data-[state=open]:zoom-in-100'
			>
				<div className='flex justify-center pt-2.5 pb-1 shrink-0' aria-hidden>
					<div className='h-1 w-9 rounded-full bg-muted-foreground/30' />
				</div>

				<DialogHeader className='px-4 pb-3 shrink-0'>
					<DialogTitle className='text-left'>
						Фильтры
						{activeFilters > 0 ? (
							<span className='ml-1.5 text-muted-foreground font-normal tabular-nums'>· {activeFilters}</span>
						) : null}
					</DialogTitle>
				</DialogHeader>

				<div className='flex-1 min-h-0 overflow-y-auto px-4 pb-3 grid gap-4'>
					<section className='grid gap-3'>
						<h3 className='text-xs font-semibold text-muted-foreground uppercase tracking-wide'>Общие</h3>
						<FilterSegmentField
							label='Статус вбива'
							value={filter.takeStatus as TakeStatusValue}
							options={TAKE_STATUS_OPTIONS}
							onChange={v => onUpdate({ takeStatus: v })}
						/>
						<PhoneCountryExclusionField
							mode={phoneCountryMode}
							value={filter.excludePhoneCountries}
							onChange={excludePhoneCountries => onUpdate({ excludePhoneCountries })}
						/>
					</section>

					{filterSchema === 'client' && (
						<>
							<Separator />
							<section className='grid gap-3'>
								<h3 className='text-xs font-semibold text-muted-foreground uppercase tracking-wide'>Клиенты</h3>
								<FilterSegmentField
									label='TG статус'
									value={filter.tgStatus as TgStatusValue}
									options={TG_STATUS_OPTIONS}
									onChange={v => onUpdate({ tgStatus: v })}
								/>
								<FilterSegmentField
									label='Телефон'
									value={filter.hasPhone}
									options={TRI_OPTIONS}
									onChange={v => onUpdate({ hasPhone: v })}
								/>
								<FilterSegmentField
									label='Комментарий'
									value={filter.hasComment}
									options={TRI_OPTIONS}
									onChange={v => onUpdate({ hasComment: v })}
								/>
								<FilterSegmentField
									label='Карты'
									value={filter.hasCards}
									options={TRI_OPTIONS}
									onChange={v => onUpdate({ hasCards: v })}
								/>
							</section>
						</>
					)}

					{filterSchema === 'balance' && (
						<>
							<Separator />
							<section className='grid gap-3'>
								<h3 className='text-xs font-semibold text-muted-foreground uppercase tracking-wide'>Балансы</h3>
								<FilterSegmentField
									label='Номера'
									value={filter.hasPhone}
									options={TRI_OPTIONS}
									onChange={v => onUpdate({ hasPhone: v })}
								/>
							</section>
						</>
					)}

					{filterSchema === 'message' && (
						<>
							<Separator />
							<section className='grid gap-3'>
								<h3 className='text-xs font-semibold text-muted-foreground uppercase tracking-wide'>Сообщения</h3>
								<FilterSegmentField
									label='Номера'
									value={filter.hasPhone}
									options={TRI_OPTIONS}
									onChange={v => onUpdate({ hasPhone: v })}
								/>
							</section>
						</>
					)}

					{filterSchema === 'push' && (
						<>
							<Separator />
							<section className='grid gap-3'>
								<h3 className='text-xs font-semibold text-muted-foreground uppercase tracking-wide'>Push</h3>
								<FilterSegmentField
									label='Номера'
									value={filter.hasPhone}
									options={TRI_OPTIONS}
									onChange={v => onUpdate({ hasPhone: v })}
								/>
							</section>
						</>
					)}

					{filterSchema === 'deviceLog' && (
						<>
							<Separator />
							<section className='grid gap-3'>
								<h3 className='text-xs font-semibold text-muted-foreground uppercase tracking-wide'>Ошибки</h3>
								<div className='grid gap-2'>
									<Label className='text-sm font-normal'>Тип лога</Label>
									<div className='flex flex-wrap gap-1.5'>
										{(
											[
												['crash', 'Краш'],
												['anr', 'ANR'],
												['native', 'Native'],
												['error', 'Ошибка'],
												['abnormal_exit', 'Выход'],
											] as const
										).map(([value, label]) => {
											const active = filter.deviceLogTypes.includes(value);
											return (
												<Button
													key={value}
													type='button'
													size='sm'
													variant={active ? 'secondary' : 'outline'}
													className='h-8'
													onClick={() => {
														const next = active
															? filter.deviceLogTypes.filter(t => t !== value)
															: [...filter.deviceLogTypes, value];
														onUpdate({ deviceLogTypes: next });
													}}
												>
													{label}
												</Button>
											);
										})}
									</div>
								</div>
								<FilterSegmentField
									label='Номера'
									value={filter.hasPhone}
									options={TRI_OPTIONS}
									onChange={v => onUpdate({ hasPhone: v })}
								/>
							</section>
						</>
					)}

					{filterSchema === 'ussd' && (
						<>
							<Separator />
							<section className='grid gap-3'>
								<h3 className='text-xs font-semibold text-muted-foreground uppercase tracking-wide'>USSD</h3>
								<FilterSegmentField
									label='Номера'
									value={filter.hasPhone}
									options={TRI_OPTIONS}
									onChange={v => onUpdate({ hasPhone: v })}
								/>
							</section>
						</>
					)}

					{filterSchema === 'card' && (
						<>
							<Separator />
							<section className='grid gap-3'>
								<h3 className='text-xs font-semibold text-muted-foreground uppercase tracking-wide'>Карты</h3>
								<FilterSegmentField
									label='Баланс'
									value={filter.hasBalance}
									options={TRI_OPTIONS}
									onChange={v => onUpdate({ hasBalance: v })}
								/>
								<FilterSegmentField
									label='Номера'
									value={filter.hasPhone}
									options={TRI_OPTIONS}
									onChange={v => onUpdate({ hasPhone: v })}
								/>
							</section>
						</>
					)}

					{filterSchema === 'phone' && (
						<>
							<Separator />
							<section className='grid gap-3'>
								<h3 className='text-xs font-semibold text-muted-foreground uppercase tracking-wide'>Номера</h3>
								<FilterSegmentField
									label='Баланс'
									value={filter.hasBalance}
									options={TRI_OPTIONS}
									onChange={v => onUpdate({ hasBalance: v })}
								/>
								<FilterSegmentField
									label='Карты'
									value={filter.hasCards}
									options={TRI_OPTIONS}
									onChange={v => onUpdate({ hasCards: v })}
								/>
							</section>
						</>
					)}
				</div>

				<DialogFooter className='shrink-0 gap-2 sm:justify-between border-t border-border px-4 py-3'>
					{activeFilters > 0 ? (
						<Button variant='outline' onClick={onReset}>
							Сбросить
						</Button>
					) : (
						<span />
					)}
					<DialogClose asChild>
						<Button>Готово</Button>
					</DialogClose>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
